/**
 * 템플릿 폼의 그림·영상 칸(2026-10-30, 반반 화면) — 콘텐츠로 올린 이미지·영상·HTML 하나를 가리킨다(`{ assetId, kind, fit }`).
 *
 * 그림·영상은 템플릿 안에 품지 않는다 — 화면이 편성과 함께 그 파일을 받아 둔다. 그래서 이 칸은 **콘텐츠를 고르는** 칸이고,
 * 내 컴퓨터의 파일은 콘텐츠로 먼저 올린 뒤 고른다(영상은 서버가 화면이 푸는 모양으로 바꾼다).
 *
 * 올린 HTML(2026-11-03)도 고른다 — 화면은 그 칸에 샌드박스로 띄운다. 맞추기는 고정 크기로 만든 HTML 에만 뜻이 있다(늘고 주는
 * HTML 은 칸 크기에 맞춰 다시 짜인다).
 */
import { useEffect, useMemo, useRef, useState, type ReactNode } from 'react';
import { htmlAreaSchema, itemHtmlFit, type HtmlArea } from '@signageone/core';
import type { Asset } from '../../api/client.js';
import { HelpTip } from '../HelpTip.js';
import { HtmlFrame } from '../HtmlContent.js';
import { HtmlAreaPicker } from './HtmlAreaPicker.js';
import { externalSummary } from '../HtmlExternalNote.js';
import { useBrand } from '../brand.js';
import { Modal } from '../ui.js';

/** 칸이 쓰는 콘솔 기능 — 템플릿 편집기가 넘긴다. */
export interface MediaFieldHost {
  /** 고를 수 있는 이미지·영상·HTML(보관하지 않은 이 고객사 콘텐츠). */
  list: () => Promise<Asset[]>;
  /** 내 컴퓨터의 파일을 콘텐츠로 올린다. */
  upload: (file: File) => Promise<Asset>;
  /** 고른 콘텐츠의 제목·미리보기. 지워졌으면 null. */
  describe: (id: string) => Promise<Asset | null>;
}

export type MediaKind = 'image' | 'video' | 'html';

export interface MediaValue {
  assetId: string;
  kind: MediaKind;
  fit: 'cover' | 'contain';
  /** HTML 에서 보일 영역(비율) — 고정 크기 HTML 에만(2026-11-03). 없으면 전체. */
  area: HtmlArea | null;
}

const KINDS: readonly string[] = ['image', 'video', 'html'];
const isKind = (value: unknown): value is MediaKind => typeof value === 'string' && KINDS.includes(value);

export function readMediaValue(raw: unknown): MediaValue | null {
  if (!raw || typeof raw !== 'object') return null;
  const value = raw as Partial<MediaValue>;
  if (typeof value.assetId !== 'string' || !isKind(value.kind)) return null;
  const area = value.kind === 'html' ? htmlAreaSchema.safeParse(value.area).data ?? null : null;
  return { assetId: value.assetId, kind: value.kind, fit: value.fit === 'contain' ? 'contain' : 'cover', area };
}

const KIND_LABEL = { image: '이미지', video: '영상', html: 'HTML' } as const;

/** 고정 크기로 만든 HTML 의 기준 크기 — 늘고 주는 HTML·"그대로" 면 null(맞추기가 뜻이 없다). */
export function htmlDesign(asset: Pick<Asset, 'width' | 'height' | 'html_fit'> | null | undefined): { width: number; height: number } | null {
  if (!asset || itemHtmlFit(asset.html_fit, asset.width, asset.height) === undefined) return null;
  return { width: asset.width as number, height: asset.height as number };
}

export function MediaField({
  label,
  help,
  error,
  value,
  host,
  disabled,
  onChange,
  emptyHint = '고르지 않았습니다 — 그 자리에 장식 무늬가 나옵니다.',
}: {
  label: string;
  help?: string;
  error?: string;
  value: MediaValue | null;
  host: MediaFieldHost | undefined;
  disabled: boolean;
  onChange: (next: MediaValue | null) => void;
  /** 고르지 않았을 때의 안내 — 구역 나누기의 [장 더하기] 칸은 다르게 말한다. */
  emptyHint?: string;
}): ReactNode {
  const [picking, setPicking] = useState(false);
  const [busy, setBusy] = useState(false);
  const [problem, setProblem] = useState<string | null>(null);
  const fileRef = useRef<HTMLInputElement>(null);
  const chosen = useChosen(host, value?.assetId ?? null);
  const { htmlOffline } = useBrand();
  const external = chosen && value?.kind === 'html' ? externalSummary(chosen.html_external, htmlOffline) : null;

  const choose = (asset: Asset): void => {
    if (!isKind(asset.type)) {
      setProblem('이미지·영상·HTML 을 고르세요');
      return;
    }
    setProblem(null);
    // 다른 콘텐츠를 고르면 보일 영역은 비운다 — 다른 문서의 자리다.
    onChange({ assetId: asset.id, kind: asset.type, fit: value?.fit ?? 'cover', area: null });
  };

  return (
    <div className="field">
      <span className="field-label">
        {label}
        {help ? <HelpTip text={help} label={`${label} 도움말`} /> : null}
      </span>
      {value ? (
        <div className="media-field__current">
          <MediaThumb asset={chosen} kind={value.kind} />
          <div className="min-w-0">
            <div className="truncate text-[13.5px] font-medium text-slate-800">
              {chosen === undefined ? '불러오는 중…' : chosen ? chosen.title : '찾을 수 없는 콘텐츠 — 다시 고르세요'}
            </div>
            <div className="text-[12px] text-slate-500">
              {KIND_LABEL[value.kind]}
              {chosen && chosen.status === 'processing' ? ' · 변환 중 — 끝나면 화면에 나옵니다' : ''}
              {chosen && value.kind === 'html' ? htmlNote(chosen) : ''}
            </div>
            {external ? <div className="text-[12px] text-amber-700">{external}</div> : null}
          </div>
        </div>
      ) : (
        <p className="hint">{emptyHint}</p>
      )}
      {host ? (
        <div className="field-row mt-2 flex-wrap">
          <button type="button" className="btn" disabled={disabled || busy} onClick={() => setPicking(true)}>
            콘텐츠에서 고르기
          </button>
          <button type="button" className="btn" disabled={disabled || busy} onClick={() => fileRef.current?.click()}>
            {busy ? '올리는 중…' : '내 컴퓨터에서 올리기'}
          </button>
          {value ? (
            <button type="button" className="btn" disabled={disabled || busy} onClick={() => onChange(null)}>
              빼기
            </button>
          ) : null}
          <input
            ref={fileRef}
            type="file"
            accept="image/*,video/*,.html,.htm,text/html"
            hidden
            onChange={(event) => {
              const file = event.target.files?.[0];
              event.target.value = '';
              if (!file) return;
              setBusy(true);
              setProblem(null);
              host.upload(file).then(
                (asset) => {
                  setBusy(false);
                  choose(asset);
                },
                (cause: unknown) => {
                  setBusy(false);
                  setProblem(cause instanceof Error ? `올리지 못했습니다 — ${cause.message}` : '올리지 못했습니다');
                },
              );
            }}
          />
        </div>
      ) : null}
      {value ? (
        <label className="field mt-2">
          <span className="field-label">맞추기</span>
          <select
            className="input"
            value={value.fit}
            disabled={disabled}
            onChange={(event) => onChange({ ...value, fit: event.target.value === 'contain' ? 'contain' : 'cover' })}
          >
            <option value="cover">가득 채우기 — 칸을 꽉 채웁니다(넘치는 곳은 잘림)</option>
            <option value="contain">전체 보이기 — 다 보입니다(남는 곳은 검게)</option>
          </select>
          {value.kind === 'html' && chosen && !htmlDesign(chosen) ? (
            <span className="hint">
              화면 크기에 맞춰 늘고 주는 HTML 이라 칸 크기에 맞춰 다시 짜입니다 — 맞추기·보일 영역은 쓰지 않습니다.
            </span>
          ) : null}
        </label>
      ) : null}
      {value?.kind === 'html' && chosen?.preview_url && htmlDesign(chosen) ? (
        <div className="field mt-2">
          <span className="field-label">보일 영역</span>
          <HtmlAreaPicker
            url={chosen.preview_url}
            title={chosen.title}
            design={htmlDesign(chosen)!}
            area={value.area}
            disabled={disabled}
            onChange={(area) => onChange({ ...value, area })}
          />
        </div>
      ) : null}
      {(problem ?? error) ? <p className="field-error">{problem ?? error}</p> : null}
      {picking && host ? (
        <MediaPicker
          host={host}
          currentId={value?.assetId ?? null}
          onClose={() => setPicking(false)}
          onPick={(asset) => {
            setPicking(false);
            choose(asset);
          }}
        />
      ) : null}
    </div>
  );
}

/** 고른 콘텐츠 — 묻는 중 undefined, 없으면 null. */
function useChosen(host: MediaFieldHost | undefined, id: string | null): Asset | null | undefined {
  const [found, setFound] = useState<{ id: string; asset: Asset | null } | null>(null);
  useEffect(() => {
    if (!host || !id) return;
    let live = true;
    host.describe(id).then(
      (asset) => live && setFound({ id, asset }),
      () => live && setFound({ id, asset: null }),
    );
    return () => {
      live = false;
    };
  }, [host, id]);
  return found && found.id === id ? found.asset : undefined;
}

/** 고른 HTML 의 크기 — 기준 크기가 있으면 그 크기로, 없으면 늘고 주는 HTML. */
function htmlNote(asset: Asset): string {
  const design = htmlDesign(asset);
  return design ? ` · 기준 크기 ${design.width}×${design.height}` : ' · 화면 크기에 맞춰 늘고 주는 HTML';
}

function MediaThumb({ asset, kind }: { asset: Asset | null | undefined; kind: MediaKind }): ReactNode {
  const url = asset?.preview_url ?? null;
  if (kind === 'html') {
    const design = htmlDesign(asset);
    return url && asset ? (
      <HtmlFrame
        url={url}
        title={asset.title}
        className="media-field__thumb"
        design={design}
        fit={design ? 'contain' : undefined}
      />
    ) : (
      <span className="media-field__thumb" aria-hidden="true" />
    );
  }
  return (
    <span className="media-field__thumb" aria-hidden="true">
      {url ? kind === 'video' ? <video src={url} muted preload="metadata" /> : <img src={url} alt="" /> : null}
    </span>
  );
}

/** 콘텐츠에서 이미지·영상·HTML 하나를 고르는 창 — 그림을 보고 고른다. */
function MediaPicker({
  host,
  currentId,
  onClose,
  onPick,
}: {
  host: MediaFieldHost;
  currentId: string | null;
  onClose: () => void;
  onPick: (asset: Asset) => void;
}): ReactNode {
  const [items, setItems] = useState<Asset[] | null>(null);
  const [error, setError] = useState<string | null>(null);
  const [query, setQuery] = useState('');
  useEffect(() => {
    let live = true;
    host.list().then(
      (found) => live && setItems(found),
      (cause: unknown) => live && setError(cause instanceof Error ? cause.message : '콘텐츠를 불러오지 못했습니다'),
    );
    return () => {
      live = false;
    };
  }, [host]);
  const shown = useMemo(() => {
    const needle = query.trim().toLowerCase();
    return (items ?? []).filter((asset) => !needle || asset.title.toLowerCase().includes(needle));
  }, [items, query]);

  return (
    <Modal title="그림·영상·HTML 고르기" onClose={onClose}>
      <input
        className="input"
        placeholder="제목으로 찾기"
        value={query}
        onChange={(event) => setQuery(event.target.value)}
        autoFocus
      />
      {error ? <p className="field-error mt-2">{error}</p> : null}
      {items === null && !error ? <p className="hint mt-3">불러오는 중…</p> : null}
      {items !== null && shown.length === 0 ? (
        <p className="hint mt-3">
          {items.length === 0
            ? '올린 이미지·영상·HTML 이 없습니다 — [내 컴퓨터에서 올리기] 로 올리세요.'
            : '찾는 제목이 없습니다.'}
        </p>
      ) : null}
      <div className="media-picker">
        {shown.map((asset) => (
          <button
            key={asset.id}
            type="button"
            className={`media-picker__item${asset.id === currentId ? ' is-current' : ''}`}
            onClick={() => onPick(asset)}
          >
            <MediaThumb asset={asset} kind={isKind(asset.type) ? asset.type : 'image'} />
            <span className="media-picker__title">{asset.title}</span>
            <span className="media-picker__kind">
              {isKind(asset.type) ? KIND_LABEL[asset.type] : '이미지'}
              {asset.status === 'processing' ? ' · 변환 중' : ''}
            </span>
          </button>
        ))}
      </div>
    </Modal>
  );
}
